import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { GoSearch } from "react-icons/go";
import { IoFilter, IoAdd } from "react-icons/io5";
import { MdDeleteOutline } from "react-icons/md";
import { CgEye } from "react-icons/cg";
import { FaSortUp, FaSortDown, FaSort } from "react-icons/fa";

// Components
import Pagination from "../../../../Admin/Components/Pagination";

// Services
import {
  deleteAfterControlFormService,
  getAfterControlFormByIdService,
  getAfterControlFormByUserIdService,
} from "../../../../Services/afterControlFormService";

// Utils
import { t } from "../../../../utils/translate";
import MainLayout from "../../../components/layout/MainLayout";

const AfterControlList = () => {
  const { id: projectId } = useParams();
  const { type, loggedInUser } = useSelector((state) => state?.auth);
  const userId = loggedInUser?._id;
  const currentLanguage = useSelector(
    (state) => state?.global?.current_language
  );

  // State management
  const [afterControlFormList, setAfterControlList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [operationType, setOperationType] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'desc'
  });

  const ITEMS_PER_PAGE = 5;

  // Fetch data on component mount
  useEffect(() => {
    getAfterControlByProjectIdApi(userId);
  }, [userId]);

  // Apply search filter
  useEffect(() => {
    if (searchTerm.trim() !== "") {
      setSearchLoading(true);
      const timer = setTimeout(() => {
        const filtered = afterControlFormList.filter(
          (control) =>
            control?.controlName
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            control?.projectId?.projectName
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            control?.projectId?.projectNumber
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );
        setFilteredList(filtered);
        setCurrentPage(1);
        setSearchLoading(false);
      }, 300); // Reduced debounce time for better UX

      return () => clearTimeout(timer);
    } else {
      setFilteredList(afterControlFormList);
    }
  }, [searchTerm, afterControlFormList]);

  // API call to fetch after control data
  const getAfterControlByProjectIdApi = async (userId) => {
    if (!userId) return;

    try {
      setIsLoading(true);
      const response = await getAfterControlFormByUserIdService(userId);

      if (response?.data) {
        const activeData = response.data
          ?.filter((item) => !item?.isDeleted)
          ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setAfterControlList(activeData);
        setFilteredList(activeData);
      }
    } catch (error) {
      console.error("Error fetching after control data:", error);
      toast.error("Failed to fetch control data");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle delete operation
  const handleOperation = (item, type) => {
    setCurrentItem(item);
    setOperationType(type);
    setIsConfirmationModalOpen(true);
  };

  // Confirm delete operation
  const handleConfirmationOperation = async () => {
    if (operationType !== "delete" || !currentItem) {
      handleCancelOperation();
      return;
    }

    setDeleteLoading(true);
    try {
      const deleteResponse = await deleteAfterControlFormService(
        currentItem?._id
      );

      if (deleteResponse?.status) {
        toast.success(deleteResponse?.message || "Successfully deleted");
        getAfterControlByProjectIdApi(userId);
      } else {
        toast.error(deleteResponse?.message || "Failed to delete");
      }
    } catch (error) {
      console.error("Error during delete operation:", error);
      toast.error("Something went wrong");
    } finally {
      setDeleteLoading(false);
      setIsConfirmationModalOpen(false);
      setCurrentItem(null);
      setOperationType(null);
    }
  };

  // Cancel operation
  const handleCancelOperation = () => {
    setIsConfirmationModalOpen(false);
    setCurrentItem(null);
    setOperationType(null);
  };

  // Sorting functionality
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = useCallback(() => {
    const sortableData = [...filteredList];
    
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        // Handle nested properties like projectId.projectName
        const keyParts = sortConfig.key.split('.');
        let aValue, bValue;
        
        if (keyParts.length > 1) {
          // For nested properties
          aValue = keyParts.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : null), a);
          bValue = keyParts.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : null), b);
        } else {
          // For direct properties
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        // Handle dates
        if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }
        
        // Handle strings (case-insensitive)
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        // If values are equal or undefined
        if (aValue === bValue || aValue === undefined || bValue === undefined) {
          return 0;
        }
        
        // Handle null values - push them to the end
        if (aValue === null) return 1;
        if (bValue === null) return -1;

        // Sort direction
        const result = aValue < bValue ? -1 : 1;
        return sortConfig.direction === 'asc' ? result : -result;
      });
    }
    
    return sortableData;
  }, [filteredList, sortConfig]);

  // Get sort icon for column
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="inline ml-1 text-gray-400" />;
    }
    
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="inline ml-1 text-blue-600" /> 
      : <FaSortDown className="inline ml-1 text-blue-600" />;
  };

  // Pagination handling
  const sortedData = getSortedData();
  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const paginatedData = sortedData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Format date for display
  const formatDateString = (isoString) => {
    if (!isoString) return "N/A";

    const date = new Date(isoString);

    const dateOptions = { year: "numeric", month: "long", day: "numeric" };
    const timeOptions = { hour: "2-digit", minute: "2-digit" };

    const formattedDate = date.toLocaleDateString("en-GB", dateOptions);
    const formattedTime = date.toLocaleTimeString("en-GB", timeOptions);

    return `${formattedDate}, ${formattedTime}`;
  };

  // Count inspection values for status
  const countInspectionValues = (data) => {
    if (!data?.afterControl)
      return { NA: 0, no: 0, yes: 0, null: 0, totalCount: 0 };

    const counts = { NA: 0, no: 0, yes: 0, null: 0, totalCount: 0 };

    data.afterControl.forEach((afterControlItem) => {
      if (!afterControlItem?.visual) return;

      afterControlItem.visual.forEach((document) => {
        counts[document.inspection || "null"]++;
        counts.totalCount++;
      });
    });

    return counts;
  };

  // Determine status color and text
  const getStatusInfo = (item) => {
    const counts = countInspectionValues(item);

    if (counts.no > 0) {
      return {
        color: "red",
        text: "Action Needed",
        bgClass: "bg-red-100 hover:bg-red-200 border-red-200",
      };
    } else if (counts.null > 0) {
      return {
        color: "amber",
        text: "Review Required",
        bgClass: "bg-amber-50 hover:bg-amber-50 border-amber-200",
      };
    } else {
      return {
        color: "emerald",
        text: "Completed",
        bgClass: "bg-emerald-100 hover:bg-emerald-200 border-emerald-200",
      };
    }
  };

  // Column definitions for sortable columns
  const columns = [
    {
      key: 'projectId.projectName',
      label: 'Project Name',
      sortable: true
    },
    {
      key: 'controlName',
      label: t("control_name"),
      sortable: true
    },
    {
      key: 'status',
      label: t("status"),
      sortable: true
    },
    {
      key: 'createdAt',
      label: t("created_at"),
      sortable: true
    },
    {
      key: 'updatedAt',
      label: t("updated_at"),
      sortable: true
    },
    {
      key: 'actions',
      label: t("actions"),
      sortable: false
    }
  ];

  return (
    // <MainLayout title={"Create After COntrol Form"}>
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Main content */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header section */}
          <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
            {/* Action buttons */}
            <div className="flex items-center gap-4">
              {type !== 2 && (
                <Link
                  to={`/admin/create-after-control`}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <IoAdd className="mr-2" size={20} />
                  {t("createAfterControlForm")}
                </Link>
              )}
            </div>

            {/* Search bar */}
            <div className="mt-6 relative">
              <div className="relative flex items-center">
                <GoSearch className="absolute left-3 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by control name..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                />
                <button className="absolute right-3 text-gray-400 hover:text-gray-600">
                  <IoFilter size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* After control list */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                          column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                        }`}
                        onClick={() => column.sortable && requestSort(column.key)}
                      >
                        <div className="flex items-center">
                          {column.label}
                          {column.sortable && getSortIcon(column.key)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {searchLoading || isLoading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg
                            className="w-10 h-10 text-blue-500 animate-spin mb-3"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span className="text-sm font-medium text-gray-500">
                            Loading data...
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => {
                      const status = getStatusInfo(item);

                      return (
                        <tr
                          key={item._id || index}
                          className={`${status.bgClass} border-l-4 border-${status.color}-500 transition-colors duration-200`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900 uppercase">
                                {item?.projectId?.projectName ||
                                  "Unnamed Project"}
                              </span>
                              <span className="text-xs text-gray-500">
                                Project Number: {item.projectId.projectNumber}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              to={`/admin/view-after-control-form/${item?.projectId?._id}/${item?._id}`}
                              className="hover:text-blue-600 transition-colors duration-200"
                            >
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-900 uppercase">
                                  {item?.controlName || "Unnamed Control"}
                                </span>
                                {item?.signature?.approver?.name && (
                                  <span className="text-xs text-gray-500">
                                    Approver: {item.signature.approver.name}
                                  </span>
                                )}
                              </div>
                            </Link>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-800`}
                            >
                              <span
                                className={`h-2 w-2 mr-1.5 rounded-full bg-${status.color}-500`}
                              ></span>
                              {status.text}
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                            {formatDateString(item?.createdAt)}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                            {formatDateString(item?.updatedAt)}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-3">
                              <Link
                                to={`/admin/view-after-control-form/${item?.projectId?._id}/${item?._id}`}
                                className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                                title="View/Edit"
                              >
                                <CgEye size={22} />
                              </Link>

                              {type !== 2 && (
                                <button
                                  onClick={() =>
                                    handleOperation(item, "delete")
                                  }
                                  className="text-red-600 hover:text-red-800 transition-colors duration-200"
                                  title="Delete"
                                >
                                  <MdDeleteOutline size={22} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-10 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center">
                          <svg
                            className="w-12 h-12 text-gray-400 mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h14a2 2 0 012 2v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                            ></path>
                          </svg>
                          <span className="font-medium">No data found</span>
                          <p className="text-sm mt-1">
                            Try adjusting your search or create a new control
                            form.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {paginatedData.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  pageInfo={"afterControlListing"}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Confirmation Modal */}
      {isConfirmationModalOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>

                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3
                    className="text-lg leading-6 font-medium text-gray-900"
                    id="modal-title"
                  >
                    Confirm {operationType}
                  </h3>

                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to {operationType} this control?
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${
                    deleteLoading ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                  onClick={handleConfirmationOperation}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>

                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={handleCancelOperation}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AfterControlList;